/**
 * Test scenarios for the prototype.
 *
 * Each one is a preset of the dials behind the fixture — what revenue arrived,
 * what the catalogue looks like, whether the period already has statements, and what
 * state a run is in. They are grouped by whether the run has started rather than by
 * which dial they move: that is how they were built, not how they are looked for.
 *
 * A scenario earns its slot only by putting the panel into a *different state*:
 * a different layout, different affordances, something blocked or empty that was
 * not before. What the statements say — negative values, carried-forward
 * balances, contracts with no revenue or no expenses — changes no state, so it
 * is not a dial: the default fixture carries all of it at once, the way a real
 * run does.
 */
export type RevenueMode = "all_settled" | "draft" | "none";
export type CatalogueMode = "typical" | "large" | "many_excluded" | "none_eligible";
export type OutcomeMode = "generating" | "partial_failure" | "completed";

export interface Scenario {
  id: string;
  label: string;
  description: string;
  revenue: RevenueMode;
  catalogue: CatalogueMode;
  outcome: OutcomeMode | null;
  /** The period already has statements, so the run is refused. */
  periodHasStatements?: boolean;
  /** Where the dev bar lands after switching. */
  entryPath: string;
  /** Still being designed — the picker flags it so a reviewer knows. */
  inProgress?: boolean;
}

const CREATE = "/royalty-statements/create";
/** Outcome scenarios seed their run under a fixed id so the bar can link to it. */
export const SCENARIO_BATCH_ID = "batch-scenario";
const BATCH = `/royalty-statements?batch=${SCENARIO_BATCH_ID}`;

function scenario(
  partial: Partial<Scenario> & Pick<Scenario, "id" | "label" | "description">,
): Scenario {
  return {
    revenue: "all_settled",
    catalogue: "typical",
    outcome: null,
    entryPath: CREATE,
    ...partial,
  };
}

export const SCENARIOS: Scenario[] = [
  scenario({
    id: "all-settled",
    label: "All settled",
    description: "The default: a settled period over a typical catalogue.",
  }),
  scenario({
    id: "draft-revenue",
    label: "Draft revenue",
    description: "One vendor still in draft; the whole run is blocked.",
    revenue: "draft",
  }),
  scenario({
    id: "no-revenue-uploaded",
    label: "No revenue uploaded",
    description: "Nothing for this period — the period card warns, every statement is zero.",
    revenue: "none",
  }),

  scenario({
    id: "large-catalogue",
    label: "Large catalogue",
    description: "130+ contracts, 300+ statements — the sheet and table at real scale.",
    catalogue: "large",
  }),
  scenario({
    id: "single-contract",
    label: "Single contract",
    description: "Panel scoped to one contract; PDF preview instead of the table.",
    entryPath: `${CREATE}?contract=contract-2`,
  }),
  scenario({
    id: "many-excluded",
    label: "Many excluded",
    description: "Skipped for every reason: no payees, no tracks, missing ISRCs, ended before the period.",
    catalogue: "many_excluded",
  }),
  scenario({
    id: "nothing-eligible",
    label: "Nothing eligible",
    description: "Every contract incomplete; generation not possible.",
    catalogue: "none_eligible",
  }),

  scenario({
    id: "generating",
    label: "Generating",
    description: "A run in progress, held part-way through.",
    outcome: "generating",
    entryPath: BATCH,
  }),
  scenario({
    id: "partial-failure",
    label: "Partial failure",
    description: "Most generated, a handful failed, with retry.",
    outcome: "partial_failure",
    entryPath: BATCH,
  }),
  scenario({
    id: "completed-batch",
    label: "Completed batch",
    description: "The statements list filtered to a finished run.",
    outcome: "completed",
    entryPath: BATCH,
  }),
  scenario({
    id: "statements-already-generated",
    label: "Statements already generated",
    description:
      "This period's statements have been generated already: the run is refused, not narrowed.",
    periodHasStatements: true,
  }),
];

/**
 * Two sections, split by the one line a reviewer already thinks along: what the
 * run is set up on, and what it looks like once it has started. Derived from
 * `outcome` rather than a field of its own — a scenario that seeds a started run
 * *is* the second kind.
 */
export const SCENARIO_SECTIONS = [
  { title: "Setting up the run", scenarios: SCENARIOS.filter((s) => s.outcome === null) },
  { title: "A run that has started", scenarios: SCENARIOS.filter((s) => s.outcome !== null) },
];

/** The default fixture: a settled half-year over a typical catalogue. */
export const DEFAULT_SCENARIO_ID = "all-settled";
const SCENARIO_KEY = "dev-mock-brs-scenario";

export function activeScenario(): Scenario {
  const id = localStorage.getItem(SCENARIO_KEY) ?? DEFAULT_SCENARIO_ID;
  return SCENARIOS.find((s) => s.id === id) ?? SCENARIOS[0];
}

/** Stores the choice. The caller reloads — the mock seeds at module load. */
export function storeScenario(id: string): void {
  localStorage.setItem(SCENARIO_KEY, id);
}
